import { FormEvent, useState } from "react"
import { Head, router } from "@inertiajs/react"
import { AppShell } from "@/components/AppShell"
import { PageHeader } from "@/components/PageHeader"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Select } from "@/components/ui/select"

type Row = {
  key: string
  english: string
  value: string
  status: "file" | "machine" | "reviewed" | "missing"
  english_changed: boolean
}

type Props = {
  locale: string
  locales: { code: string; name: string }[]
  filter: string
  q: string
  rows: Row[]
  pagination: { page: number; pages: number; total: number; per_page: number }
}

const FILTERS = [
  { value: "unreviewed", label: "Needs review" },
  { value: "english_changed", label: "English changed" },
  { value: "missing", label: "Missing" },
  { value: "reviewed", label: "Reviewed" },
  { value: "all", label: "All" },
]

export default function TranslationsIndex({ locale, locales, filter, q, rows, pagination }: Props) {
  const [query, setQuery] = useState(q)

  const go = (params: Record<string, string | number>) =>
    router.get("/admin/translations", { locale, filter, q: query, ...params }, { preserveScroll: false })

  const search = (e: FormEvent) => {
    e.preventDefault()
    go({ page: 1 })
  }

  return (
    <>
      <Head title="Translations">
        <meta name="description" content="Review and edit the app's text in each language." />
        <meta property="og:title" content="Translations" />
        <meta property="og:description" content="Review and edit the app's text in each language." />
      </Head>
      <AppShell>
        <PageHeader
          title="Translations"
          description="English is on the left and can't be changed here. Edit the translation on the right, then save. Keep the {placeholders} and <tags> as they are."
        />

        {locales.length === 0 ? (
          <p className="mt-8">You haven&rsquo;t been given a language to review yet. Ask an admin.</p>
        ) : (
          <>
            <form onSubmit={search} className="mt-8 flex flex-wrap items-end gap-3">
              <div className="space-y-1.5">
                <label htmlFor="tr-locale">Language</label>
                <Select id="tr-locale" value={locale} onChange={(e) => go({ locale: e.target.value, page: 1 })}>
                  {locales.map((l) => (
                    <option key={l.code} value={l.code}>
                      {l.name}
                    </option>
                  ))}
                </Select>
              </div>
              <div className="space-y-1.5">
                <label htmlFor="tr-filter">Show</label>
                <Select id="tr-filter" value={filter} onChange={(e) => go({ filter: e.target.value, page: 1 })}>
                  {FILTERS.map((f) => (
                    <option key={f.value} value={f.value}>
                      {f.label}
                    </option>
                  ))}
                </Select>
              </div>
              <div className="min-w-48 flex-1 space-y-1.5">
                <label htmlFor="tr-search">Search</label>
                <Input id="tr-search" type="search" value={query} onChange={(e) => setQuery(e.target.value)} />
              </div>
              <Button type="submit" variant="secondary">
                Search
              </Button>
            </form>

            <p className="mt-4 text-sm text-ink-muted" role="status">
              {pagination.total} {pagination.total === 1 ? "message" : "messages"}
            </p>

            <ul className="mt-4 space-y-4 p-0">
              {rows.map((row) => (
                <RowEditor key={`${locale}:${row.key}:${row.value}`} locale={locale} row={row} />
              ))}
            </ul>

            {pagination.pages > 1 && (
              <nav className="mt-8 flex items-center justify-between" aria-label="Pages">
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  disabled={pagination.page <= 1}
                  onClick={() => go({ page: pagination.page - 1 })}
                >
                  Previous
                </Button>
                <span className="text-sm text-ink-muted">
                  Page {pagination.page} of {pagination.pages}
                </span>
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  disabled={pagination.page >= pagination.pages}
                  onClick={() => go({ page: pagination.page + 1 })}
                >
                  Next
                </Button>
              </nav>
            )}
          </>
        )}
      </AppShell>
    </>
  )
}

function RowEditor({ locale, row }: { locale: string; row: Row }) {
  const [value, setValue] = useState(row.value)
  const [saving, setSaving] = useState(false)
  const dirty = value !== row.value
  const id = `tr-${row.key}`

  const save = (reviewed: boolean) =>
    router.patch(
      "/admin/translations",
      { locale, key: row.key, value, reviewed },
      { preserveScroll: true, onStart: () => setSaving(true), onFinish: () => setSaving(false) },
    )

  return (
    <li className="list-none rounded-xl border border-hairline bg-page p-4">
      <div className="flex flex-wrap items-center gap-2">
        <code className="break-all text-xs text-ink-muted">{row.key}</code>
        {row.english_changed && <Badge tone="signal">English changed</Badge>}
        {row.status === "reviewed" && !row.english_changed && <Badge tone="accent">Reviewed</Badge>}
        {row.status === "machine" && !row.english_changed && <Badge tone="neutral">Needs review</Badge>}
        {row.status === "file" && <Badge tone="muted">Not reviewed</Badge>}
        {row.status === "missing" && <Badge tone="signal">Missing</Badge>}
      </div>
      <div className="mt-3 grid gap-4 md:grid-cols-2">
        <div>
          <span className="text-xs text-ink-muted">English</span>
          <p className="mt-1 whitespace-pre-wrap break-words">{row.english}</p>
        </div>
        <div className="space-y-1.5">
          <label htmlFor={id}>Translation</label>
          <textarea
            id={id}
            className="form-control form-control-textarea min-h-20"
            value={value}
            onChange={(e) => setValue(e.target.value)}
          />
          <div className="flex flex-wrap gap-2">
            <Button type="button" size="sm" variant="secondary" disabled={saving || !dirty || !value.trim()} onClick={() => save(false)}>
              Save
            </Button>
            <Button type="button" size="sm" disabled={saving || !value.trim()} onClick={() => save(true)}>
              {dirty ? "Save and mark reviewed" : "Mark reviewed"}
            </Button>
          </div>
        </div>
      </div>
    </li>
  )
}
